import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Upload, X, Camera, ImagePlus, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL;

/**
 * ImageUploader — direct file upload from phone/camera or computer.
 * Uploads each file to /api/products/upload-image, gets back a URL,
 * and calls onUploaded with the full absolute URL.
 *
 * Props:
 *  - onUploaded(url: string) — callback fired after each successful upload
 *  - maxFiles: number — cap for one session (default 5)
 *  - testIdPrefix: string — for data-testid attributes
 *  - compact: bool — smaller UI variant
 */
export default function ImageUploader({ onUploaded, maxFiles = 5, testIdPrefix = 'img', compact = false }) {
  const [uploading, setUploading] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const inputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const uploadOne = async (file) => {
    if (!file) return null;
    if (file.size > 8 * 1024 * 1024) {
      toast.error(`${file.name} is too big (max 8 MB)`);
      return null;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const r = await axios.post(`${API}/products/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Convert relative URL → absolute URL so it works when copy-pasted
      const rel = r.data.url;
      const absolute = rel.startsWith('http') ? rel : `${BACKEND_ORIGIN}${rel}`;
      return absolute;
    } catch (err) {
      const msg = err.response?.data?.detail || `Upload failed for ${file.name}`;
      toast.error(msg);
      return null;
    }
  };

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList).slice(0, maxFiles);
    setUploading(true);
    setUploadedCount(0);
    let success = 0;
    for (const file of files) {
      const url = await uploadOne(file);
      if (url) {
        success++;
        setUploadedCount(success);
        if (onUploaded) onUploaded(url);
      }
    }
    setUploading(false);
    if (success > 0) {
      toast.success(`${success} photo${success > 1 ? 's' : ''} uploaded`);
    }
    // Reset the file input so the same file can be re-picked
    if (inputRef.current) inputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  if (compact) {
    return (
      <div className="inline-flex gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid={`${testIdPrefix}-file-input`}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="text-xs"
          data-testid={`${testIdPrefix}-upload-btn`}
        >
          {uploading ? (
            <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Uploading {uploadedCount}...</>
          ) : (
            <><ImagePlus className="h-3.5 w-3.5 mr-1" />Upload photos</>
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border-2 border-dashed border-border p-4 bg-muted/30" data-testid={`${testIdPrefix}-uploader`}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testIdPrefix}-file-input`}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testIdPrefix}-camera-input`}
      />

      {uploading ? (
        <div className="flex flex-col items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span>Uploading photo {uploadedCount + 1}...</span>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="text-center text-xs text-muted-foreground">
            Choose photos from your phone, camera roll, or take a new one
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              data-testid={`${testIdPrefix}-choose-btn`}
              className="flex-1 min-w-[140px]"
            >
              <Upload className="h-4 w-4 mr-2" />
              Choose photos
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => cameraInputRef.current?.click()}
              data-testid={`${testIdPrefix}-camera-btn`}
              className="flex-1 min-w-[140px]"
            >
              <Camera className="h-4 w-4 mr-2" />
              Take photo
            </Button>
          </div>
          <div className="text-center text-[10px] text-muted-foreground/70">
            JPEG · PNG · WEBP · HEIC · up to 8 MB each · up to {maxFiles} photos
          </div>
        </div>
      )}
    </div>
  );
}
